class HeaderComponent extends HTMLElement {
  constructor() {
    super();

    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.measureHeaderHeights = this.measureHeaderHeights.bind(this);
  }

  connectedCallback() {
    // select element
    this.headerElement = this;
    this.headerWrapper = document.querySelector('.shopify-section-group-header-group');
    this.announcementBar = document.querySelector('.section-announcement-bar');
    this.popovers = this.querySelectorAll('[popover]');
    this.popoverTriggers = this.querySelectorAll('button[popovertarget]');
    this.headerHeight = 0;
    this.announcementBarHeight = 0;
    this.headerHeightFull = 0;

    if (this.headerWrapper) {
      this.currentScrollTop = 0;
      this.scrollOffset = 0;
      this.headerStart = 0;
      this.prevStateScrollTop = 0;
      this.scrollThreshold = 50;

      this.measureHeaderHeights();

      // scroll event
      this.request = null;
      this.timer = null;
      this._scrollAnimation = this.scrollAnimation.bind(this);
      window.addEventListener('window:scroll', this.handleScroll);

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        if (this.headerElement) this.resizeObserver.observe(this.headerElement);
        if (this.announcementBar) this.resizeObserver.observe(this.announcementBar);
      }
    }

    this.initPopoverTriggers();
  }

  disconnectedCallback() {
    window.removeEventListener('window:scroll', this.handleScroll);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    this.popoverTriggers.forEach((trigger) => {
      trigger.removeEventListener('click', this.popoverTriggersHandler);
    });
  }

  handleScroll() {
    clearTimeout(this.timer);
    if (!this.request) this.request = requestAnimationFrame(this._scrollAnimation);
    this.timer = setTimeout(() => {
      cancelAnimationFrame(this.request);
      this.request = null;
    }, 50);
  }

  handleResize() {
    this.measureHeaderHeights();
  }

  measureHeaderHeights() {
    const announcementBarHeight = this.announcementBar?.offsetHeight || 0;
    const headerHeight = this.headerElement?.offsetHeight || 0;
    const headerHeightFull = announcementBarHeight + headerHeight;

    this.announcementBarHeight = announcementBarHeight;
    this.headerHeight = headerHeight;
    this.headerHeightFull = headerHeightFull;
    this.scrollOffset = headerHeightFull;
    this.headerStart = announcementBarHeight;

    // Distance from the header top to the desktop menu row, used as the negative sticky
    // offset that lets the top row scroll out of view. Measured via rects because the
    // top row's block margins collapse out of its own offsetHeight.
    const desktopMenu = this.querySelector('.header__desktop-menu');
    let topRowHeight = 0;
    if (desktopMenu && desktopMenu.offsetParent !== null) {
      topRowHeight = Math.max(0, desktopMenu.getBoundingClientRect().top - this.headerElement.getBoundingClientRect().top);
    }

    // set global css var for the header height
    document.documentElement.style.setProperty('--header-height-full', `${headerHeightFull}px`);
    document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    document.documentElement.style.setProperty('--announcement-bar-height', `${announcementBarHeight}px`);
    document.documentElement.style.setProperty('--header-top-row-height', `${topRowHeight}px`);
  }

  initPopoverTriggers() {
    const useHover = !window.interestForElementSupported && document.documentElement.classList.contains('no-touch');

    this.popoverTriggers.forEach((trigger) => {
      if (useHover) {
        ['click', 'mouseenter'].forEach(event => trigger.addEventListener(event, event => this.openPopover(event, trigger)));
      }
      else {
        trigger.addEventListener('click', (event) => {
          const popover = document.getElementById(trigger.getAttribute('popovertarget'));
          if (popover?.matches(':popover-open') && trigger.dataset?.url && trigger.dataset.url !== '#') {
            event.preventDefault();
            event.stopPropagation();
            window.location.href = trigger.dataset.url;
          }
        });
      }
    });
  }

  openPopover(event, trigger) {
    if (!trigger) return;
    const popover = document.getElementById(trigger.getAttribute('popovertarget'));
    if (!popover) return;

    if (event.type === 'click' && popover.matches(':popover-open') && trigger.dataset?.url && trigger.dataset.url !== '#') {
      window.location.href = trigger.dataset.url;
      return;
    }

    clearTimeout(this.openTimer);

    this.openTimer = setTimeout(() => popover.showPopover({ source: trigger }), 800);

    trigger.addEventListener('mouseleave', () => clearTimeout(this.openTimer), { once: true });

    if (!popover._popoverMouseleave) {
      popover._popoverMouseleave = true;
      popover.addEventListener('mouseleave', () => this.closePopover(popover));
    }

    this.activePopover = popover;
  }

  closePopover(popover) {
    const target = popover || this.activePopover;
    if (target) setTimeout(() => target.hidePopover(), 800);
  }

  closeAllPopovers() {
    Array.from(this.popovers)
      .find(popover => popover.matches(':popover-open'))
      ?.hidePopover();
  }

  scrollAnimation() {
    // The mobile menu's position is coupled to the header-sticky/header-hidden classes, so the
    // header state must not change while a dialog is open — background scrolling is allowed,
    // but it must not move the dialog
    if (document.querySelector('dialog[open]')) {
      this.request = requestAnimationFrame(this._scrollAnimation);
      return;
    }

    // Short viewports (e.g. 200% browser zoom): the header is not sticky (see reset.css), so
    // keep the body state classes off too — .header-hidden would otherwise translate the
    // static header out of its own box and leave a blank strip behind
    if (window.windowHeight <= 480) {
      if (document.body.classList.contains('header-sticky')) this.revealTop();
      this.request = requestAnimationFrame(this._scrollAnimation);
      return;
    }

    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const isScrollingDown = scrollTop > this.currentScrollTop;
    const isScrollingUp = scrollTop < this.currentScrollTop;

    if (scrollTop <= this.headerStart) {
      this.revealTop();
      this.prevStateScrollTop = scrollTop;
    }
    else if (isScrollingUp && (this.prevStateScrollTop - scrollTop) >= this.scrollThreshold) {
      this.reveal();
      this.prevStateScrollTop = scrollTop;
    }
    else if (scrollTop + window.windowHeight > document.body.clientHeight) {
      this.hide();
      this.prevStateScrollTop = scrollTop;
    }
    else if (isScrollingDown && (scrollTop - this.prevStateScrollTop) >= this.scrollThreshold && scrollTop > this.scrollOffset) {
      this.hide();
      this.prevStateScrollTop = scrollTop;
    }

    this.currentScrollTop = scrollTop;

    this.request = requestAnimationFrame(this._scrollAnimation);
  }

  hide() {
    document.body.classList.add('header-hidden', 'header-sticky');
    this.popovers.forEach(popover => popover.hidePopover());
  }

  reveal() {
    document.body.classList.add('header-sticky');
    document.body.classList.remove('header-hidden');
    document.documentElement.style.setProperty('--header-height', `${this.headerHeight}px`);
  }

  revealTop() {
    document.body.classList.remove('header-hidden', 'header-sticky');
    document.documentElement.style.setProperty('--header-height-full', `${this.headerHeightFull}px`);
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}
